import { createPublicClient, createWalletClient, http, custom } from '@arkiv-network/sdk';
import { ExpirationTime, jsonToPayload } from '@arkiv-network/sdk/utils';
import { eq } from '@arkiv-network/sdk/query';
import { mendoza } from '@arkiv-network/sdk/chains';
import type { BusinessCard } from './types';

// Create public client for reading
export const publicClient = createPublicClient({
  chain: mendoza,
  transport: http(),
});

// Create wallet client for writing using browser wallet
export async function createArkivWalletClient() {
  // Check if wallet is available
  if (typeof window === 'undefined' || !window.ethereum) {
    throw new Error('No wallet provider found. Please install MetaMask or another compatible wallet.');
  }

  // Request accounts to ensure we have access
  const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });

  if (!accounts || accounts.length === 0) {
    throw new Error('No accounts found. Please connect your wallet.');
  }

  const walletClient = createWalletClient({
    chain: mendoza,
    transport: custom(window.ethereum), // Use browser wallet provider
    account: accounts[0], // Use the first connected account
  });

  return walletClient;
}

// Save business card to Arkiv
export async function saveCardToArkiv(
  walletClient: any,
  card: BusinessCard
): Promise<{ entityKey: string; txHash: string }> {
  try {
    const payloadCard: BusinessCard = { ...card, status: 'confirmed' };
    const result = await walletClient.createEntity({
      payload: jsonToPayload(payloadCard),
      contentType: 'application/json',
      attributes: [
        { key: 'type', value: 'business-card' },
        { key: 'name', value: card.name },
        { key: 'lat', value: card.location.coordinates.lat.toString() },
        { key: 'lng', value: card.location.coordinates.lng.toString() },
        { key: 'wallet', value: card.walletAddress.toLowerCase() },
        { key: 'updated', value: new Date().toISOString() },
      ],
      expiresIn: ExpirationTime.fromDays(365), // Card expires in 1 year
    });

    return result;
  } catch (error) {
    console.error('Error saving to Arkiv:', error);
    throw error;
  }
}

// Get all business cards for a wallet address
export async function getCardsByWallet(walletAddress: string) {
  try {
    const query = publicClient.buildQuery();
    const result = await query
      .where(eq('type', 'business-card'))
      .where(eq('wallet', walletAddress.toLowerCase()))
      .withAttributes(true)
      .withPayload(true)
      .withMetadata(true)
      .limit(100)
      .fetch();

    const cards: BusinessCard[] = [];

    for (const entity of result.entities) {
      try {
        if (entity.payload) {
          const cardData = JSON.parse(
            new TextDecoder().decode(entity.payload)
          ) as BusinessCard;
          cardData.arkivKey = entity.key;
          cardData.expiresAt = entity.expiresAt;
          cardData.status = 'confirmed';
          cards.push(cardData);
        }
      } catch (err) {
        console.error('Error parsing card data:', err);
      }
    }

    return cards;
  } catch (error) {
    console.error('Error fetching cards from Arkiv:', error);
    return [];
  }
}

// Get a single card by entity key
export async function getCardByKey(entityKey: string): Promise<BusinessCard | null> {
  try {
    const entity = await publicClient.getEntity(entityKey, { withMetadata: true });

    if (entity && entity.payload) {
      const cardData = JSON.parse(
        new TextDecoder().decode(entity.payload)
      ) as BusinessCard;
      cardData.arkivKey = entity.key;
      cardData.expiresAt = entity.expiresAt;
      return cardData;
    }

    return null;
  } catch (error) {
    console.error('Error fetching card from Arkiv:', error);
    return null;
  }
}

// Update business card on Arkiv
export async function updateCardOnArkiv(
  walletClient: any,
  entityKey: string,
  card: BusinessCard,
  newExpiration?: Date
): Promise<{ txHash: string }> {
  try {
    const payloadCard: BusinessCard = { ...card, status: 'confirmed' };
    const BLOCK_TIME_SECONDS = 2; // Arkiv block time
    const expirationFromDate = (date?: Date | null) => {
      if (!date) return null;
      const timestamp = date.getTime();
      return Number.isFinite(timestamp) ? ExpirationTime.fromDate(date) : null;
    };

    const computedExpires = expirationFromDate(newExpiration) ??
      expirationFromDate(card.expiresAt ? new Date(card.expiresAt) : null) ??
      ExpirationTime.fromDays(365);

    const expiresInSeconds = Math.max(ExpirationTime.fromSeconds(60), computedExpires);
    // Align to block time to avoid fractional values in tx encoding
    const alignedExpires = Math.max(
      BLOCK_TIME_SECONDS,
      Math.floor(expiresInSeconds / BLOCK_TIME_SECONDS) * BLOCK_TIME_SECONDS
    );

    const result = await walletClient.updateEntity({
      entityKey,
      payload: jsonToPayload(payloadCard),
      contentType: 'application/json',
      attributes: [
        { key: 'type', value: 'business-card' },
        { key: 'name', value: card.name },
        { key: 'lat', value: card.location.coordinates.lat.toString() },
        { key: 'lng', value: card.location.coordinates.lng.toString() },
        { key: 'wallet', value: card.walletAddress.toLowerCase() },
        { key: 'updated', value: new Date().toISOString() },
      ],
      expiresIn: alignedExpires,
    });

    return result;
  } catch (error) {
    console.error('Error updating on Arkiv:', error);
    throw error;
  }
}

// Delete business card from Arkiv
export async function deleteCardOnArkiv(
  walletClient: any,
  entityKey: string
): Promise<{ txHash: string }> {
  try {
    const result = await walletClient.deleteEntity({ entityKey });
    return result;
  } catch (error) {
    console.error('Error deleting from Arkiv:', error);
    throw error;
  }
}

// Get all business cards (for globe view)
export async function getAllBusinessCards(limit = 1000) {
  try {
    const query = publicClient.buildQuery();
    const result = await query
      .where(eq('type', 'business-card'))
      .withAttributes(true)
      .withPayload(true)
      .limit(limit)
      .fetch();

    const cards: BusinessCard[] = [];

    for (const entity of result.entities) {
      try {
        if (entity.payload) {
          const cardData = JSON.parse(
            new TextDecoder().decode(entity.payload)
          ) as BusinessCard;
          cardData.arkivKey = entity.key;
          cardData.status = 'confirmed';
          cards.push(cardData);
        }
      } catch (err) {
        console.error('Error parsing card data:', err);
      }
    }

    return cards;
  } catch (error) {
    console.error('Error fetching all cards from Arkiv:', error);
    return [];
  }
}
